(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const initialized = new WeakSet();
  let observer = null;
  let scrollTicking = false;
  let activeHero = null;

  function setReveal(element, delay = 0, direction = 'up') {
    if (!element || initialized.has(element)) return;
    initialized.add(element);
    element.dataset.elaReveal = direction;
    element.style.setProperty('--ela-delay', `${delay}ms`);
  }

  function revealNow(element) {
    if (!element) return;
    element.classList.add('is-visible');
  }

  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        revealNow(entry.target);
        observer.unobserve(entry.target);
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -8% 0px'
    });

    return observer;
  }

  function watch(element) {
    if (!element) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      revealNow(element);
      return;
    }

    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.93 && rect.bottom > 0) {
      requestAnimationFrame(() => revealNow(element));
      return;
    }

    getObserver()?.observe(element);
  }

  function setupHero(main) {
    const hero = main.querySelector('.hero');
    if (!hero) return;

    hero.dataset.elaHero = 'true';
    activeHero = hero;

    const content = hero.querySelector('.hero__content-wrapper');
    if (!content) return;

    const items = Array.from(content.children).filter((node) => node instanceof HTMLElement);
    items.forEach((item, index) => {
      setReveal(item, index * 95, index === items.length - 1 ? 'scale' : 'up');
    });

    requestAnimationFrame(() => {
      items.forEach((item) => revealNow(item));
    });
  }

  function setupSections(main) {
    const topSections = Array.from(main.children).filter((node) =>
      node instanceof HTMLElement && (node.classList.contains('shopify-section') || node.classList.contains('section-wrapper'))
    );

    topSections.forEach((section, index) => {
      if (section.querySelector('.hero')) return;

      const primary = section.querySelector(
        '.section-content-wrapper, .product-information, [data-testid="ui-test-section"], main-page, .main-page'
      ) || section.firstElementChild;

      if (primary instanceof HTMLElement) {
        setReveal(primary, Math.min(index * 55, 180), index % 2 === 0 ? 'up' : 'scale');
        watch(primary);
      }
    });

    main.querySelectorAll('.section-content-wrapper').forEach((container) => {
      const directGroups = Array.from(container.children).filter((child) =>
        child instanceof HTMLElement && child.classList.contains('group-block')
      );

      if (directGroups.length >= 3) {
        directGroups.forEach((card, index) => {
          card.dataset.elaCard = 'true';
          setReveal(card, index * 95, 'up');
          watch(card);
        });
      }
    });
  }

  function setupImages(main) {
    const storytellingSection = main.querySelector('[id*="section_MwKWXA"]');
    if (!storytellingSection) return;

    storytellingSection.querySelectorAll('img').forEach((image) => {
      const shell = image.closest('.image-block, [data-testid="image-block"], .spacing-style') || image.parentElement;
      if (shell instanceof HTMLElement) shell.dataset.elaImageShell = 'true';
    });
  }

  function setupProduct(main) {
    const template = main.dataset.template || '';
    if (!template.includes('product')) return;

    const gallery = main.querySelector('media-gallery, .media-gallery, [data-testid*="media-gallery"]');
    const details = main.querySelector('.product-information .group-block, .product-details, [class*="product-details"]');

    if (gallery instanceof HTMLElement) {
      setReveal(gallery, 0, 'left');
      watch(gallery);
    }

    if (details instanceof HTMLElement) {
      setReveal(details, 90, 'right');
      watch(details);
    }
  }

  function initialize(root = document) {
    const main = root === document
      ? document.getElementById('MainContent')
      : root.querySelector?.('#MainContent') || document.getElementById('MainContent');

    if (!main) return;

    setupHero(main);
    setupSections(main);
    setupImages(main);
    setupProduct(main);

    document.documentElement.classList.add('ela-motion-ready');

    main.querySelectorAll('[data-ela-reveal]').forEach((element) => {
      if (!element.classList.contains('is-visible')) watch(element);
    });
  }

  function updateScrollEffects() {
    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.classList.toggle('ela-header-scrolled', scrollY > 24);

    if (activeHero && !reduceMotion.matches && finePointer.matches && window.innerWidth >= 900) {
      const heroRect = activeHero.getBoundingClientRect();
      if (heroRect.bottom > 0 && heroRect.top < window.innerHeight) {
        const movement = Math.max(-12, Math.min(14, scrollY * 0.035));
        activeHero.style.setProperty('--ela-parallax', `${movement}px`);
      }
    }

    scrollTicking = false;
  }

  function onScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(updateScrollEffects);
  }

  function start() {
    initialize(document);
    updateScrollEffects();
    window.addEventListener('scroll', onScroll, { passive: true });

    document.addEventListener('shopify:section:load', (event) => {
      initialize(event.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
